import NotFrankfurtPolygon from '../json/not_ffm.json'

import type { User } from './Types'
import type { Feature, Position } from 'geojson'
import type mapboxgl from 'mapbox-gl'
import type { LngLatBoundsLike } from 'mapbox-gl'

export { parseIssueAttributeValueJson as parseValueJson } from './Types/issueJson'

export type LngLat = [lng: number, lat: number]
export type LngLatAlt = [lng: number, lat: number, alt: number]

export function getLngLat(position: Position): LngLat {
  const lng = position[0]
  const lat = position[1]
  if (lng === undefined || lat === undefined) {
    throw new Error('Invalid GeoJSON position: missing longitude or latitude')
  }
  return [lng, lat]
}

export function isHtmlInputEvent(event: Event): event is Event & { target: HTMLInputElement } {
  return 'target' in event && event.target instanceof HTMLInputElement
}

export function hasFiles(
  event: Event,
): event is Event & { target: HTMLInputElement & { files: File[] } } {
  return isHtmlInputEvent(event) && (event.target.files?.length ?? 0) > 0
}

export async function resizeImage(image: File, maxWidth: number) {
  const blob =
    'OffscreenCanvas' in globalThis
      ? await resizeImageWithOffscreenCanvas(image, maxWidth)
      : await resizeImageWithCanvas(image, maxWidth)

  return new File([blob], image.name.replace(/\.[^/.]+$/, '.jpg'), { type: 'image/jpeg' })
}

async function resizeImageWithOffscreenCanvas(image: File, maxWidth: number) {
  const bitmap = await createImageBitmap(image)
  const aspect = bitmap.height / bitmap.width

  const canvas = new OffscreenCanvas(maxWidth, maxWidth * aspect)
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Could not get canvas context')

  context.drawImage(bitmap, 0, 0, maxWidth, maxWidth * aspect)
  bitmap.close()

  return canvas.convertToBlob({ type: 'image/jpeg' })
}

async function resizeImageWithCanvas(image: File, maxWidth: number) {
  const bitmap = await createImageBitmap(image)
  const aspect = bitmap.height / bitmap.width

  const canvas = document.createElement('canvas')
  canvas.width = maxWidth
  canvas.height = maxWidth * aspect
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Could not get canvas context')

  context.drawImage(bitmap, 0, 0, maxWidth, maxWidth * aspect)
  bitmap.close()

  return toBlob(canvas, 'image/jpeg')
}

async function toBlob(canvas: HTMLCanvasElement, type: string) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('Could not convert canvas to blob'))
      }
    }, type)
  })
}

export function highlightFrankfurt(map: mapboxgl.Map, fillColor = 'black') {
  map.addSource('not-frankfurt', {
    type: 'geojson',
    // JSON imports widen GeoJSON's discriminant strings, despite this static file being a Feature.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    data: NotFrankfurtPolygon as Feature,
  })

  map.addLayer({
    id: 'not-frankfurt',
    type: 'fill',
    source: 'not-frankfurt',
    slot: 'bottom',
    paint: {
      'fill-color': fillColor,
      'fill-opacity': 0.25,
    },
  })
}

export function isCoordinateInFrankfurt(map: mapboxgl.Map, coordinate: mapboxgl.LngLatLike) {
  const [notFrankfurtFeature] = map.queryRenderedFeatures(map.project(coordinate), {
    layers: ['not-frankfurt'],
  })
  return !notFrankfurtFeature
}

export const PADDED_FRANKFURT_BOUNDS = [
  [8.051003, 49.703355],
  [9.484237, 50.431327],
] as LngLatBoundsLike

export const FES_HEADQUARTER_POINT: GeoJSON.Feature<GeoJSON.Point> = {
  type: 'Feature',
  geometry: {
    type: 'Point',
    coordinates: [8.682127, 50.110924],
  },
  properties: {},
}

export async function loadImage(map: mapboxgl.Map, url: string) {
  return new Promise<HTMLImageElement | ImageBitmap>((resolve, reject) => {
    map.loadImage(url, (error, image) => {
      if (error) {
        reject(error)
        return
      }
      if (!image || !(image instanceof HTMLImageElement || image instanceof ImageBitmap)) {
        reject(new Error('Invalid image format'))
        return
      }

      resolve(image)
    })
  })
}

export function assert(x: unknown): asserts x {
  if (!x) throw new Error('Assertion failed')
}

export function translateIllegalDumpingType(illegalDumpingType: string) {
  const illegalDumpingsMap: Record<string, string> = {
    'small-dumping': 'Wilde Ablagerung',
    'nitrous-oxide-canister': 'Lachgaskartusche',
  }

  return illegalDumpingsMap[illegalDumpingType] || illegalDumpingType
}

export function isAdmin(user: User): boolean {
  return user.roles?.some((role) => role.label === 'Admin') ?? false
}

export function openUrlInNewWindow(url: string | URL) {
  return globalThis.open(url, '_blank')
}
